import { usePeriodFilter, TIME_RANGES } from '../../context/PeriodFilterContext';

interface PeriodSelectorBarProps {
  showExtraActions?: React.ReactNode;
}

export default function PeriodSelectorBar({ showExtraActions }: PeriodSelectorBarProps) {
  const {
    selectedRange,
    setSelectedRange,
    monthOffset,
    canGoNext,
    prevMonth,
    nextMonth,
    resetMonth,
    dateRangeDisplay,
  } = usePeriodFilter();

  return (
    <div className="flex flex-wrap items-center gap-2.5">
      {/* Period Filter Pills */}
      <div className="inline-flex rounded-xl bg-white dark:bg-boxdark p-1 border border-brand-line dark:border-strokedark shadow-card">
        {TIME_RANGES.map((range) => (
          <button
            key={range}
            type="button"
            onClick={() => setSelectedRange(range)}
            className={`px-3 py-1 text-xs font-mono font-semibold rounded-lg transition-colors cursor-pointer ${
              selectedRange === range
                ? 'bg-brand-vib text-white shadow-sm'
                : 'text-brand-muted hover:text-brand-navy dark:text-bodydark dark:hover:text-white'
            }`}
          >
            {range}
          </button>
        ))}
      </div>

      {/* Date Indicator & Month Stepper Navigation */}
      {selectedRange === 'Month' ? (
        <div className="inline-flex items-center gap-1 p-1 rounded-xl bg-white dark:bg-boxdark border border-brand-line dark:border-strokedark shadow-card">
          {/* Previous Month Arrow */}
          <button
            type="button"
            onClick={prevMonth}
            title="Previous Month"
            className="w-7 h-7 flex items-center justify-center rounded-lg text-brand-navy dark:text-white hover:bg-brand-bg dark:hover:bg-strokedark transition-colors cursor-pointer"
          >
            <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </button>

          {/* Month Label with Calendar Icon */}
          <div className="flex items-center gap-1.5 px-2 text-xs font-mono font-semibold text-brand-navy dark:text-white">
            <svg className="w-3.5 h-3.5 text-brand-vib fill-current" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                clipRule="evenodd"
              />
            </svg>
            <span>{dateRangeDisplay}</span>
          </div>

          {/* Next Month Arrow (Disabled when at present month) */}
          <button
            type="button"
            onClick={nextMonth}
            disabled={!canGoNext}
            title={canGoNext ? 'Next Month' : 'Present month is latest available'}
            className={`w-7 h-7 flex items-center justify-center rounded-lg transition-colors ${
              canGoNext
                ? 'text-brand-navy dark:text-white hover:bg-brand-bg dark:hover:bg-strokedark cursor-pointer'
                : 'text-brand-soft/30 dark:text-bodydark2/30 cursor-not-allowed opacity-30'
            }`}
          >
            <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </button>

          {/* Reset to Current Month (when offset is active) */}
          {monthOffset !== 0 && (
            <button
              type="button"
              onClick={resetMonth}
              title="Reset to Current Month"
              className="px-2 py-1 text-[10px] font-mono font-semibold rounded-md bg-brand-blue-tint text-brand-vib hover:bg-brand-vib hover:text-white dark:bg-brand-900/40 dark:text-brand-300 transition-colors cursor-pointer"
            >
              Current
            </button>
          )}
        </div>
      ) : (
        <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white dark:bg-boxdark border border-brand-line dark:border-strokedark shadow-card text-xs font-mono text-brand-navy dark:text-white">
          <svg className="w-3.5 h-3.5 text-brand-vib fill-current" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
              clipRule="evenodd"
            />
          </svg>
          <span>{dateRangeDisplay}</span>
        </div>
      )}

      {/* Optional Extra Action Buttons (e.g. Export CSV, Quick Links) */}
      {showExtraActions}
    </div>
  );
}
